import { useState } from 'react'
import { Icon } from '@iconify/react'
import type { ChecklistTopic as ChecklistTopicType } from '@/types/checklist'
import { ChecklistItem } from './ChecklistItem'
import styles from './ChecklistTopic.module.css'

interface ChecklistTopicProps {
  topic: ChecklistTopicType
  onUpdateTopic: (updates: { title?: string }) => void
  onRemoveTopic: () => void
  onMoveTopic: (direction: 'up' | 'down') => void
  onAddItem: () => void
  onUpdateItem: (
    itemId: string,
    updates: {
      text?: string
      completed?: boolean
      dueDate?: string
      note?: string
    }
  ) => void
  onRemoveItem: (itemId: string) => void
  onMoveItem: (itemId: string, direction: 'up' | 'down') => void
  onAddSubitem: (itemId: string) => void
  onUpdateSubitem: (
    subitemId: string,
    updates: { text?: string; completed?: boolean }
  ) => void
  onRemoveSubitem: (subitemId: string) => void
  onMoveSubitem: (
    itemId: string,
    subitemId: string,
    direction: 'up' | 'down'
  ) => void
  canMoveUp: boolean
  canMoveDown: boolean
}

export function ChecklistTopic({
  topic,
  onUpdateTopic,
  onRemoveTopic,
  onMoveTopic,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onMoveItem,
  onAddSubitem,
  onUpdateSubitem,
  onRemoveSubitem,
  onMoveSubitem,
  canMoveUp,
  canMoveDown,
}: ChecklistTopicProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editTitle, setEditTitle] = useState(topic.title)
  const [expanded, setExpanded] = useState(true)

  const handleTitleBlur = () => {
    setIsEditingTitle(false)
    const trimmed = editTitle.trim()
    if (trimmed !== topic.title) {
      onUpdateTopic({ title: trimmed || 'Tópico' })
    }
    setEditTitle(topic.title)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur()
    if (e.key === 'Escape') {
      setEditTitle(topic.title)
      setIsEditingTitle(false)
      e.currentTarget.blur()
    }
  }

  const topicProgress =
    topic.items.length > 0
      ? (() => {
          let completed = 0
          let total = 0
          for (const item of topic.items) {
            total += 1
            if (item.completed) completed += 1
            for (const sub of item.subitems) {
              total += 1
              if (sub.completed) completed += 1
            }
          }
          return { completed, total }
        })()
      : null

  return (
    <section className={styles.topic} aria-labelledby={`topic-${topic.id}`}>
      <div className={styles.topicHeader}>
        <button
          type="button"
          className={styles.expandBtn}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`topic-content-${topic.id}`}
        >
          <Icon
            icon={expanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
            className={styles.expandIcon}
          />
        </button>
        <div className={styles.topicTitleWrap}>
          {isEditingTitle ? (
            <input
              id={`topic-${topic.id}`}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              className={styles.titleInput}
              autoFocus
              aria-label="Editar título do tópico"
            />
          ) : (
            <h2
              id={`topic-${topic.id}`}
              className={styles.topicTitle}
              onClick={() => {
                setIsEditingTitle(true)
                setEditTitle(topic.title)
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsEditingTitle(true)
                  setEditTitle(topic.title)
                }
              }}
            >
              {topic.title || 'Tópico'}
            </h2>
          )}
          {topicProgress !== null && (
            <span className={styles.topicProgress}>
              {topicProgress.completed}/{topicProgress.total}
            </span>
          )}
        </div>
        <div className={styles.topicActions}>
          <button
            type="button"
            onClick={() => onMoveTopic('up')}
            disabled={!canMoveUp}
            className={styles.iconBtn}
            aria-label="Subir tópico"
          >
            <Icon icon="mdi:chevron-up" />
          </button>
          <button
            type="button"
            onClick={() => onMoveTopic('down')}
            disabled={!canMoveDown}
            className={styles.iconBtn}
            aria-label="Descer tópico"
          >
            <Icon icon="mdi:chevron-down" />
          </button>
          <button
            type="button"
            onClick={onAddItem}
            className={styles.addItemBtn}
            aria-label="Adicionar item"
          >
            <Icon icon="mdi:plus" />
            Item
          </button>
          <button
            type="button"
            onClick={onRemoveTopic}
            className={styles.iconBtnDanger}
            aria-label="Remover tópico"
          >
            <Icon icon="mdi:trash-can-outline" />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          id={`topic-content-${topic.id}`}
          className={styles.topicContent}
        >
          {topic.items.length === 0 ? (
            <p className={styles.emptyTopic}>
              Nenhum item. Clique em &quot;Item&quot; para adicionar.
            </p>
          ) : (
            <ul className={styles.itemsList}>
              {topic.items.map((item, index) => (
                <li key={item.id}>
                  <ChecklistItem
                    item={item}
                    onUpdate={(updates) => onUpdateItem(item.id, updates)}
                    onRemove={() => onRemoveItem(item.id)}
                    onMove={(dir) => onMoveItem(item.id, dir)}
                    onAddSubitem={() => onAddSubitem(item.id)}
                    onUpdateSubitem={onUpdateSubitem}
                    onRemoveSubitem={onRemoveSubitem}
                    onMoveSubitem={(subitemId, dir) =>
                      onMoveSubitem(item.id, subitemId, dir)
                    }
                    canMoveUp={index > 0}
                    canMoveDown={index < topic.items.length - 1}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
